import { LockOutlined } from '@ant-design/icons';
import { Form, Input, Button, Modal, App as AntApp } from 'antd';
import { useTranslation } from 'react-i18next';

import { useChangePasswordMutation } from '../api/authApi';

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

export function ChangePasswordModal({ open, onClose }: ChangePasswordModalProps) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const [changePassword, { isLoading }] = useChangePasswordMutation();

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      await changePassword({
        current_password: values.current_password,
        new_password: values.new_password,
      }).unwrap();
      message.success(t('auth.changePasswordSuccess'));
      form.resetFields();
      onClose();
    } catch {
      message.error(t('auth.changePasswordFailed'));
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={t('auth.changePasswordTitle')}
      open={open}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          {t('common.cancel', { defaultValue: 'Hủy' })}
        </Button>,
        <Button key="submit" type="primary" loading={isLoading} onClick={handleOk}>
          {t('auth.changePasswordSubmit')}
        </Button>,
      ]}
      destroyOnHidden
      centered
      width={420}
    >
      <Form form={form} layout="vertical" autoComplete="off" className="pt-2">
        <Form.Item
          name="current_password"
          rules={[{ required: true, message: t('auth.currentPasswordRequired') }]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={t('auth.currentPassword')}
            autoComplete="current-password"
          />
        </Form.Item>
        <Form.Item
          name="new_password"
          rules={[
            { required: true, message: t('auth.pleaseEnterPassword') },
            { min: 8, message: t('auth.passwordMinLength') },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={t('auth.newPassword')}
            autoComplete="new-password"
          />
        </Form.Item>
        <Form.Item
          name="confirm_password"
          dependencies={['new_password']}
          rules={[
            { required: true, message: t('auth.pleaseConfirmPassword') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('new_password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('auth.passwordMismatch')));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={t('auth.confirmNewPassword')}
            autoComplete="new-password"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
